import { useCallback, useEffect, useState } from "react";
import { FiRefreshCw, FiExternalLink, FiXCircle, FiCopy, FiCheck } from "react-icons/fi";

import {
  listOrders,
  getOrder,
  cancelOrder,
  getOrderStatus,
  formatBRL,
  formatDateTime,
  getErrorMessage,
  ORDER_STATUS_LABELS,
} from "../services/billingApi";
import NoData from "../components/NoData";
import "./FaturasPage.css";

const PAYMENT_LABELS = {
  PIX: "PIX",
  CARD: "Cartão",
  BOLETO: "Boleto",
  PREFERENCE: "Mercado Pago",
};

const FaturasPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOrders(await listOrders());
      setError(null);
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Não foi possível carregar suas faturas."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleDetail = async (orderId) => {
    if (expandedId === orderId) {
      setExpandedId(null);
      setDetail(null);
      return;
    }

    setExpandedId(orderId);
    setDetail(null);

    try {
      setDetail(await getOrder(orderId));
    } catch {
      setDetail(null);
    }
  };

  /** Reconsulta o gateway — útil quando o pagamento caiu mas a tela não atualizou. */
  const handleRefreshStatus = async (orderId) => {
    setBusyId(orderId);
    try {
      await getOrderStatus(orderId);
      await load();
    } catch (refreshError) {
      setError(getErrorMessage(refreshError, "Não foi possível atualizar o status."));
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm("Cancelar este pedido pendente?")) return;

    setBusyId(orderId);
    try {
      await cancelOrder(orderId);
      await load();
    } catch (cancelError) {
      setError(getErrorMessage(cancelError, "Não foi possível cancelar."));
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyPix = async (code, paymentId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(paymentId);
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      setCopiedId(null);
    }
  };

  return (
    <div className="faturas fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Faturas e pagamentos</h2>
          <p>Histórico completo das suas assinaturas, destaques e serviços contratados.</p>
        </div>
        <button type="button" className="button button--ghost" onClick={load} disabled={loading}>
          <FiRefreshCw /> Atualizar
        </button>
      </div>

      {error && <div className="faturas__error">{error}</div>}

      <div className="section-card">
        {loading ? (
          <p className="muted">Carregando faturas...</p>
        ) : orders.length === 0 ? (
          <NoData
            title="Nenhuma fatura ainda"
            description="Quando você contratar um plano ou serviço, os pagamentos aparecem aqui."
          />
        ) : (
          <div className="faturas__list">
            {orders.map((order) => {
              const statusInfo = ORDER_STATUS_LABELS[order.status] || {
                label: order.status,
                tone: "info",
              };
              const isExpanded = expandedId === order.id;
              const isPendingPayable = ["PENDING", "IN_PROCESS"].includes(order.status);

              return (
                <div key={order.id} className={`fatura${isExpanded ? " fatura--open" : ""}`}>
                  <button
                    type="button"
                    className="fatura__row"
                    onClick={() => toggleDetail(order.id)}
                  >
                    <div className="fatura__main">
                      <strong>{order.description}</strong>
                      <small>
                        {formatDateTime(order.createdAt)}
                        {order.paymentMethod
                          ? ` · ${PAYMENT_LABELS[order.paymentMethod] || order.paymentMethod}`
                          : ""}
                        {order.installments > 1 ? ` · ${order.installments}x` : ""}
                      </small>
                    </div>

                    <span className="fatura__amount">{formatBRL(order.amountCents)}</span>
                    <span className="badge" data-tone={statusInfo.tone}>
                      {statusInfo.label}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="fatura__detail">
                      {order.failureReason && (
                        <p className="fatura__reason">Motivo: {order.failureReason}</p>
                      )}

                      {detail?.payments?.length > 0 && (
                        <div className="fatura__payments">
                          {detail.payments.map((payment) => (
                            <div key={payment.id} className="fatura__payment">
                              <div>
                                <strong>#{payment.mpPaymentId}</strong>
                                <small>
                                  {payment.status}
                                  {payment.statusDetail ? ` · ${payment.statusDetail}` : ""}
                                </small>
                              </div>

                              {payment.pixQrCode && order.status !== "PAID" && (
                                <button
                                  type="button"
                                  className="fatura__pix-copy"
                                  onClick={() => handleCopyPix(payment.pixQrCode, payment.id)}
                                >
                                  {copiedId === payment.id ? <FiCheck /> : <FiCopy />}
                                  {copiedId === payment.id ? "Copiado" : "Copiar PIX"}
                                </button>
                              )}

                              {payment.ticketUrl && (
                                <a
                                  className="fatura__link"
                                  href={payment.ticketUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <FiExternalLink /> Comprovante
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="fatura__actions">
                        {isPendingPayable && (
                          <>
                            {order.initPoint && (
                              <a
                                className="button button--primary"
                                href={order.initPoint}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FiExternalLink /> Pagar agora
                              </a>
                            )}
                            <button
                              type="button"
                              className="button button--ghost"
                              onClick={() => handleRefreshStatus(order.id)}
                              disabled={busyId === order.id}
                            >
                              <FiRefreshCw /> Já paguei, verificar
                            </button>
                            <button
                              type="button"
                              className="button button--ghost"
                              onClick={() => handleCancel(order.id)}
                              disabled={busyId === order.id}
                            >
                              <FiXCircle /> Cancelar
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FaturasPage;
